import React, { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Camera, CheckCircle2, Clock, Download, FileText, MapPin, Receipt, Shield, X } from "lucide-react";
import { API_BASE_URL } from "../../../lib/axios";
import type { PassageApiItem, TourneeApiItem } from "../../planification-logistique/planificateur/planningTypes";
import { clientLabel } from "../../planification-logistique/planificateur/planningDashboardUtils";
import {
  gpsBadgeKind,
  photoFraction,
  validationLabel,
  validationUi,
} from "./passageExecutionUtils";

const PassageGpsMapPanel = lazy(() => import("./PassageGpsMapPanel"));

function fmtDt(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function fmtMeters(m?: number | null): string {
  if (m == null || Number.isNaN(m)) return "—";
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${Math.round(m)} m`;
}

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function statutBadgeClass(statut: string): string {
  const s = statut.toUpperCase();
  if (s === "EFFECTUE" || s === "PARTIEL") return "bg-emerald-500/20 text-emerald-200 ring-emerald-500/35";
  if (s === "EN_ATTENTE_VALIDATION") return "bg-amber-500/20 text-amber-100 ring-amber-500/35";
  if (s === "CLOSED_WITH_ANOMALIES") return "bg-orange-500/20 text-orange-100 ring-orange-500/35";
  if (s === "NON_EFFECTUE") return "bg-rose-500/20 text-rose-100 ring-rose-500/35";
  return "bg-slate-700/60 text-slate-200 ring-slate-600/50";
}

function apiPublicRoot(): string {
  return API_BASE_URL.replace(/\/api\/?$/i, "").replace(/\/+$/, "");
}

function absolutePublicUrl(raw?: string | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  const root = apiPublicRoot();
  return root ? `${root}${value.startsWith("/") ? value : `/${value}`}` : value;
}

function preuvePhotoUrl(passage: PassageApiItem, type: "AVANT" | "APRES"): string | null {
  const preuve = (passage.preuves ?? []).find((p) => String(p.type ?? "").toUpperCase() === type);
  return absolutePublicUrl(preuve?.photoUrl);
}

export type PassageExecutionDrawerProps = {
  open: boolean;
  passage: PassageApiItem | null;
  tournee?: TourneeApiItem | null;
  onClose: () => void;
  /** Scroll / focus section when opening from actions row */
  initialSection?: "gps" | "photos" | null;
};

type TimelineItem = { key: string; label: string; detail: string; done: boolean };
type PassageDetailTab = "summary" | "terrain" | "gps" | "billing" | "history";

const TABS: Array<{ id: PassageDetailTab; label: string }> = [
  { id: "summary", label: "Résumé" },
  { id: "terrain", label: "Terrain" },
  { id: "gps", label: "GPS" },
  { id: "billing", label: "Facturation" },
  { id: "history", label: "Historique" },
];

export function PassageExecutionDrawer(props: PassageExecutionDrawerProps): React.JSX.Element | null {
  const { open, passage, tournee, onClose, initialSection } = props;
  const panelRef = useRef<HTMLElement>(null);
  const titleId = useId();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PassageDetailTab>("summary");

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (lightboxUrl) {
          setLightboxUrl(null);
          return;
        }
        onClose();
      }
      if (e.key === "Tab" && panelRef.current && !lightboxUrl) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [lightboxUrl, onClose]
  );

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    const t = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const focusable = root.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 50);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", handleKeyDown);
      window.clearTimeout(t);
    };
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (!open) setLightboxUrl(null);
  }, [open, passage?.id]);

  useEffect(() => {
    if (!open) return;
    if (initialSection === "gps") setActiveTab("gps");
    else if (initialSection === "photos") setActiveTab("terrain");
    else setActiveTab("summary");
  }, [open, initialSection, passage?.id]);

  const mapCenter = useMemo((): [number, number] => {
    const DEFAULT_CENTER: [number, number] = [-11.6642, 27.4794];
    if (!passage) return DEFAULT_CENTER;
    const sLat = passage.startLatitude;
    const sLng = passage.startLongitude;
    const eLat = passage.endLatitude;
    const eLng = passage.endLongitude;
    const startOk =
      sLat != null && sLng != null && !Number.isNaN(sLat) && !Number.isNaN(sLng);
    const endOk = eLat != null && eLng != null && !Number.isNaN(eLat) && !Number.isNaN(eLng);
    if (startOk && endOk) return [(sLat + eLat) / 2, (sLng + eLng) / 2];
    if (startOk) return [sLat, sLng];
    if (endOk) return [eLat, eLng];
    return DEFAULT_CENTER;
  }, [passage]);

  if (!open || !passage) return null;

  const gps = gpsBadgeKind(passage);
  const photos = photoFraction(passage);
  const val = validationUi(passage);
  const st = passage.statut?.toUpperCase() ?? "";

  const startOk =
    passage.startLatitude != null &&
    passage.startLongitude != null &&
    !Number.isNaN(passage.startLatitude) &&
    !Number.isNaN(passage.startLongitude);
  const endOk =
    passage.endLatitude != null &&
    passage.endLongitude != null &&
    !Number.isNaN(passage.endLatitude) &&
    !Number.isNaN(passage.endLongitude);

  const interDistance =
    startOk && endOk
      ? haversineMeters(passage.startLatitude!, passage.startLongitude!, passage.endLatitude!, passage.endLongitude!)
      : null;

  const validatedByLabel =
    passage.validatedBy != null
      ? [passage.validatedBy.prenom, passage.validatedBy.nom].filter(Boolean).join(" ").trim() ||
        `Utilisateur #${passage.validatedBy.id}`
      : null;

  const timeline: TimelineItem[] = [
    { key: "c", label: "Passage créé", detail: fmtDt(passage.datePrevue), done: true },
    {
      key: "t",
      label: "Affecté tournée",
      detail: passage.tournee?.numero ? `Tournée ${passage.tournee.numero}` : "—",
      done: passage.tourneeId != null,
    },
    { key: "s", label: "Terrain démarré", detail: fmtDt(passage.startedAt), done: Boolean(passage.startedAt) },
    {
      key: "g",
      label: "GPS relevé",
      detail: startOk || endOk ? "Coordonnées enregistrées" : "Non relevé",
      done: startOk || endOk,
    },
    {
      key: "p",
      label: "Photos ajoutées",
      detail: photos.label,
      done: photos.n >= 2,
    },
    {
      key: "v",
      label: "Validation superviseur",
      detail: validatedByLabel ? `${validatedByLabel} · ${fmtDt(passage.validatedAt)}` : fmtDt(passage.validatedAt),
      done: val === "valide" || val === "rejete" || val === "clos_anomalies",
    },
    {
      key: "b",
      label: "Facturation",
      detail: passage.billingLabel ?? (passage.isFacturable ? "Facturable" : "Non facturable"),
      done: Boolean(passage.billingStatus && passage.billingStatus !== "PENDING_EVALUATION"),
    },
  ];

  const badges: { label: string; tone: "slate" | "emerald" | "amber" | "rose" | "violet" }[] = [];
  if (["EFFECTUE", "PARTIEL", "CLOSED_WITH_ANOMALIES", "EN_ATTENTE_VALIDATION", "NON_EFFECTUE"].includes(st)) {
    badges.push({ label: "Réalisé / traité terrain", tone: "emerald" });
  }
  if (st === "EN_ATTENTE_VALIDATION") badges.push({ label: "En validation", tone: "amber" });
  if (gps === "suspect") badges.push({ label: "GPS suspect", tone: "rose" });
  if (passage.isFacturable) badges.push({ label: "Facturable", tone: "violet" });
  else badges.push({ label: "Non facturable", tone: "slate" });
  if (photos.n < 2) badges.push({ label: "Photos incomplètes", tone: "amber" });

  const badgeTone =
    (tone: (typeof badges)[number]["tone"]) =>
      ({
        slate: "bg-slate-800 text-slate-200 ring-slate-600/50",
        emerald: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30",
        amber: "bg-amber-500/15 text-amber-100 ring-amber-500/35",
        rose: "bg-rose-500/15 text-rose-100 ring-rose-500/35",
        violet: "bg-violet-500/15 text-violet-100 ring-violet-500/35",
      })[tone];

  const duree =
    typeof passage.dureeReelleMin === "number" && passage.dureeReelleMin > 0
      ? `${passage.dureeReelleMin} min`
      : passage.startedAt && passage.endedAt
        ? `${Math.max(1, Math.round((new Date(passage.endedAt).getTime() - new Date(passage.startedAt).getTime()) / 60000))} min`
        : "—";
  const startPosition: [number, number] | null = startOk ? [passage.startLatitude!, passage.startLongitude!] : null;
  const endPosition: [number, number] | null = endOk ? [passage.endLatitude!, passage.endLongitude!] : null;
  const slaLabel = passage.slaLabelFr ?? passage.passageSlaStatus ?? "—";
  const conformityLabel =
    gps === "ok" && photos.n >= 2 && !passage.isSuspect
      ? "Conforme"
      : gps === "suspect" || passage.isSuspect
        ? "À contrôler"
        : "À compléter";

  const InfoCard = ({
    label,
    value,
    tone = "default",
  }: {
    label: string;
    value: React.ReactNode;
    tone?: "default" | "success" | "warning";
  }) => (
    <div
      className={`rounded-2xl border p-4 ${
        tone === "success"
          ? "border-emerald-500/25 bg-emerald-950/15"
          : tone === "warning"
            ? "border-amber-500/25 bg-amber-950/15"
            : "border-slate-800 bg-slate-950/35"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-semibold text-slate-100">{value}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-6" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/55 backdrop-blur-sm transition-opacity"
        aria-label="Fermer la modale"
        onClick={onClose}
      />
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-950 shadow-2xl md:h-[90vh] md:max-h-[90vh] md:max-w-[1100px] md:rounded-3xl md:border md:border-slate-700/90"
      >
        <header className="shrink-0 border-b border-slate-800 bg-slate-950/95 px-4 py-4 md:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Détail passage</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h2 id={titleId} className="text-xl font-semibold tracking-tight text-white">
                  Passage #{passage.id}
                </h2>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${statutBadgeClass(passage.statut)}`}>
                  {passage.displayLabel ?? passage.statut}
                </span>
              </div>
              <p className="mt-1 truncate text-sm text-slate-300">{clientLabel(passage)}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {badges.map((b) => (
                  <span key={b.label} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${badgeTone(b.tone)}`}>
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-slate-700 p-2.5 text-slate-400 transition hover:bg-slate-900 hover:text-white"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Sections détail passage">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition ${
                  activeTab === tab.id
                    ? "bg-sky-500 text-white shadow-lg shadow-sky-950/40"
                    : "border border-slate-800 bg-slate-900/60 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 text-sm text-slate-300 md:px-6">
          {activeTab === "summary" ? (
            <div className="space-y-5" role="tabpanel">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <InfoCard label="Client" value={clientLabel(passage)} />
                <InfoCard label="Zone" value={passage.zone?.nom ?? "—"} />
                <InfoCard label="Tournée" value={passage.tournee?.numero ?? (passage.tourneeId ? `#${passage.tourneeId}` : "—")} />
                <InfoCard label="Équipe" value={tournee?.equipe?.nom ?? "—"} />
                <InfoCard label="Date prévue" value={fmtDt(passage.datePrevue)} />
                <InfoCard label="Statut" value={passage.displayLabel ?? passage.statut} />
                <InfoCard label="Durée" value={duree} />
                <InfoCard label="SLA" value={slaLabel} tone={slaLabel === "—" ? "default" : "success"} />
                <InfoCard label="Conformité" value={conformityLabel} tone={conformityLabel === "Conforme" ? "success" : "warning"} />
              </div>
              <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <Shield className="h-4 w-4 text-sky-400" />
                  Contexte métier
                </h3>
                <dl className="grid gap-3 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="text-slate-500">Contrat</dt>
                    <dd className="mt-0.5 font-mono text-slate-200">#{passage.contratId}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Mission / intervention</dt>
                    <dd className="mt-0.5 text-slate-200">
                      {passage.mission?.numero ? `Mission ${passage.mission.numero}` : "—"}
                      {passage.intervention?.titre ? ` · ${passage.intervention.titre}` : ""}
                    </dd>
                  </div>
                </dl>
              </section>
            </div>
          ) : null}

          {activeTab === "terrain" ? (
            <div className="grid gap-5 lg:grid-cols-[1fr_340px]" role="tabpanel">
              <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <MapPin className="h-4 w-4 text-emerald-400" />
                  Exécution terrain
                </h3>
                <dl className="grid gap-3 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="text-slate-500">Début / fin terrain</dt>
                    <dd className="mt-0.5 text-slate-200">
                      {fmtDt(passage.startedAt)} → {fmtDt(passage.endedAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Statut agent</dt>
                    <dd className="mt-0.5 font-mono text-[11px] text-slate-300">{passage.executionStatus ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Véhicule</dt>
                    <dd className="mt-0.5 text-slate-200">{tournee?.vehicule?.immatriculation ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Chauffeur</dt>
                    <dd className="mt-0.5 text-slate-200">
                      {tournee?.chauffeur ? [tournee.chauffeur.nom, tournee.chauffeur.prenom].filter(Boolean).join(" ") : "—"}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-slate-500">Commentaire terrain</dt>
                    <dd className="mt-0.5 whitespace-pre-wrap rounded-lg bg-slate-950/60 p-3 text-slate-200">
                      {passage.commentaire?.trim() || "—"}
                    </dd>
                  </div>
                </dl>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <Camera className="h-4 w-4 text-violet-400" />
                  Médias terrain
                </h3>
                <div className="grid gap-3">
                  {(["before", "after"] as const).map((kind) => {
                    const rawUrl =
                      kind === "before"
                        ? passage.beforePhotoUrl ?? preuvePhotoUrl(passage, "AVANT")
                        : passage.afterPhotoUrl ?? preuvePhotoUrl(passage, "APRES");
                    const url = absolutePublicUrl(rawUrl);
                    const label = kind === "before" ? "Photo avant" : "Photo après";
                    return (
                      <div key={kind} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/45">
                        <p className="border-b border-slate-800 px-3 py-2 text-[11px] font-semibold text-slate-400">{label}</p>
                        <div className="flex aspect-[16/10] items-center justify-center p-2">
                          {url ? (
                            <button
                              type="button"
                              className="relative h-full w-full overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/60"
                              onClick={() => setLightboxUrl(url)}
                            >
                              <img src={url} alt={label} className="h-full w-full object-cover" loading="lazy" />
                            </button>
                          ) : (
                            <div className="px-3 text-center text-xs text-slate-500">Aucune image</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(passage.preuves ?? []).length > 0 ? (
                  <ul className="mt-4 space-y-2 text-xs">
                    {(passage.preuves ?? []).map((pr) => {
                      const prUrl = absolutePublicUrl(pr.photoUrl);
                      return (
                        <li key={pr.id} className="flex justify-between gap-2 rounded-lg bg-slate-950/45 px-3 py-2">
                          {prUrl ? (
                            <button type="button" className="font-medium text-sky-200 hover:underline" onClick={() => setLightboxUrl(prUrl)}>
                              {pr.type}
                            </button>
                          ) : (
                            <span className="font-medium text-slate-200">{pr.type}</span>
                          )}
                          <span className="text-slate-500">{fmtDt(pr.createdAt)}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </section>
            </div>
          ) : null}

          {activeTab === "gps" ? (
            <section className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/40 p-5" role="tabpanel">
              {(gps === "suspect" || passage.isSuspect) ? (
                <div className="rounded-2xl border border-amber-500/25 bg-amber-950/15 px-4 py-3 text-xs text-amber-100">
                  Anomalie GPS à vérifier : les coordonnées ou distances demandent un contrôle superviseur.
                </div>
              ) : null}
              <dl className="grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                <InfoCard label="GPS début" value={startOk ? `${passage.startLatitude!.toFixed(5)}, ${passage.startLongitude!.toFixed(5)}` : "—"} />
                <InfoCard label="GPS fin" value={endOk ? `${passage.endLatitude!.toFixed(5)}, ${passage.endLongitude!.toFixed(5)}` : "—"} />
                <InfoCard label="Distance client" value={`${fmtMeters(passage.distanceStartClientM)} / ${fmtMeters(passage.distanceEndClientM)}`} />
                <InfoCard label="Distance début → fin" value={interDistance != null ? fmtMeters(interDistance) : "—"} />
                <InfoCard label="Conformité GPS" value={gps === "ok" ? "Conforme" : gps === "suspect" ? "À contrôler" : "Non relevé"} tone={gps === "ok" ? "success" : gps === "suspect" ? "warning" : "default"} />
              </dl>
              {startOk || endOk ? (
                <Suspense fallback={<div className="h-64 rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-xs text-slate-500">Chargement de la carte...</div>}>
                  <PassageGpsMapPanel center={mapCenter} start={startPosition} end={endPosition} />
                </Suspense>
              ) : (
                <p className="text-xs text-slate-500">Aucune coordonnée cartographiable pour ce passage.</p>
              )}
            </section>
          ) : null}

          {activeTab === "billing" ? (
            <div className="grid gap-5 lg:grid-cols-2" role="tabpanel">
              <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <Receipt className="h-4 w-4 text-violet-400" />
                  Facturation
                </h3>
                <dl className="grid gap-3 text-xs">
                  <InfoCard label="Statut facturation" value={passage.billingLabel ?? (passage.isFacturable ? "Facturable" : "Non facturable")} tone={passage.isFacturable ? "success" : "default"} />
                  <InfoCard label="Facture liée" value={passage.facture?.numero ?? "—"} />
                  <div>
                    <dt className="text-slate-500">Motif / note</dt>
                    <dd className="mt-1 rounded-xl bg-slate-950/50 p-3 text-slate-300">
                      {passage.billingReason ?? "—"}
                      {passage.billingNote ? ` · ${passage.billingNote}` : ""}
                    </dd>
                  </div>
                </dl>
              </section>
              <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <CheckCircle2 className="h-4 w-4 text-amber-400" />
                  Validation superviseur
                </h3>
                <dl className="grid gap-3 text-xs">
                  <InfoCard label="Décision" value={val === "terrain" ? "Terrain (non clos)" : val === "en_attente" ? "Validation requise" : validationLabel(val)} />
                  <InfoCard label="Validé par" value={validatedByLabel ?? "—"} />
                  <InfoCard label="Date validation" value={fmtDt(passage.validatedAt)} />
                  <div>
                    <dt className="text-slate-500">Commentaire validation</dt>
                    <dd className="mt-1 whitespace-pre-wrap rounded-xl bg-slate-950/50 p-3 text-slate-300">
                      {passage.commentaireValidation?.trim() || passage.motifNonExecution?.trim() || "—"}
                    </dd>
                  </div>
                </dl>
              </section>
            </div>
          ) : null}

          {activeTab === "history" ? (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" role="tabpanel">
              <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">Timeline métier</h3>
              <ol className="relative ms-1 space-y-5 border-l border-slate-700 py-1 pl-6">
                {timeline.map((ev) => (
                  <li key={ev.key} className="relative">
                    <span className={`absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-slate-950 ${ev.done ? "bg-emerald-400" : "bg-slate-600"}`} />
                    <p className="text-[13px] font-semibold text-slate-100">{ev.label}</p>
                    <p className="text-xs text-slate-500">{ev.detail}</p>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-800 bg-slate-950/95 px-4 py-3 md:px-6">
          <a
            href={`/admin/historique-passages?passageId=${passage.id}`}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-slate-900"
          >
            <FileText className="h-4 w-4" />
            Voir page complète
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-slate-900"
          >
            <Download className="h-4 w-4" />
            Export PDF
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(gps === "suspect" || passage.isSuspect ? "gps" : "billing")}
            className="rounded-xl border border-amber-500/30 px-3 py-2 text-xs font-semibold text-amber-100 transition hover:bg-amber-950/30"
          >
            Corriger anomalie
          </button>
          <button type="button" onClick={onClose} className="rounded-xl bg-sky-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-sky-400">
            Fermer
          </button>
        </footer>
      </section>

      {lightboxUrl ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-zoom-out"
            aria-label="Fermer la visionneuse"
            onClick={() => setLightboxUrl(null)}
          />
          <img
            src={lightboxUrl}
            alt="Preuve plein écran"
            className="relative z-10 max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
          />
        </div>
      ) : null}
    </div>
  );
}
